import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ArrowForward } from '@mui/icons-material';
import { getCourses } from '../../Utils/apiService';
import { getUserDetails } from '../../storageService';
import KenLoader from '../../GlobalComponents/KenLoader';
import { useNavigate } from 'react-router-dom';
import routes from '../../Utils/routes';
import { CourseSf } from '../../interfaces/Common';

interface CourseCardListProps {
  academics?: boolean;
  icon?: boolean;
  date?: boolean;
  limit?: number;
  viewMode?: 'all' | 'my-programs';
  cardStyle?: React.CSSProperties;
  iconStyle?: React.CSSProperties;
  titleStyle?: React.CSSProperties;
  enrolledTextStyle?: React.CSSProperties;
  searchQuery?: string;
  onCardClick?: (courseId: string) => void;
}

const useStyles = makeStyles(() => ({
  formControl: {
    width: '300px',
    marginBottom: '16px',
  },
}));

const CourseCardList: React.FC<CourseCardListProps> = ({
  academics = false,
  icon = true,
  date = true,
  limit = 6,
  viewMode = 'all',
  cardStyle = {},
  iconStyle = {},
  titleStyle = {},
  enrolledTextStyle = {},
  searchQuery = '',
  onCardClick,
}) => {
  const [courses, setCourses] = useState<CourseSf[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [selectedTerm, setSelectedTerm] = useState('');
  const classes = useStyles();

  useEffect(() => {
    fetchCourses();
  }, [limit, viewMode, searchQuery]);

  const fetchCourses = async () => {
    setIsLoading(true);
    try {
      const user = await getUserDetails();
      const data: CourseSf[] = await getCourses(user?.ContactId);
      setCourses(data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardClick = (courseId: string) => {
    navigate(`${routes.courseElaborate}?courseid=${courseId}`);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <KenLoader />
      </Box>
    );
  }

  return (
    <>
      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 2,
          paddingLeft: 2,
        }}
      >
        <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
          Programs taught
        </Typography>
        <Select
          value={selectedTerm}
          onChange={(e) => setSelectedTerm(e.target.value)}
          displayEmpty
          sx={{
            width: '250px',
            height: '48px',
            borderRadius: '8px',
            border: '1px solid #B2B3B7',
            backgroundColor: 'white',
          }}
        >
          <MenuItem value="" disabled>
            <Typography style={{ color: 'grey', textAlign: 'left' }}>
              Select Program
            </Typography>
          </MenuItem>
          {courses.map((course) => (
            <MenuItem key={course.Id} value={course.ProgramName}>
              {course.ProgramName}
            </MenuItem>
          ))}
        </Select>
      </Grid>

      <Grid container spacing={3} justifyContent="flex-start" sx={{ px: 2 }}>
        {courses
          ?.filter((course) => !selectedTerm || course.ProgramName === selectedTerm)

          ?.map((course, index) => {
            const firstLetter = course.ProgramName.match(/[A-Za-z]/)?.[0]?.toUpperCase() || '?';

            return (
              <Tooltip title="Please click here to view the program plan" arrow key={index}>
                <Grid item xs={12} sm={6} md={4}>
                  {academics ? (
                    <Card
                      style={{
                        cursor: 'pointer',
                        boxShadow: 'none',
                        padding: 12,
                        borderRadius: '0.75rem',
                        width: '100%',
                        position: 'relative',
                        ...cardStyle,
                      }}
                    >
                      {icon && (
                        <Box
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 8,
                            color: '#fff',
                            backgroundColor: '#3f51b5',
                            ...iconStyle,
                          }}
                        >
                          {firstLetter}
                        </Box>
                      )}

                      <Box
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 8,
                        }}
                      >
                        <Tooltip title={course.ProgramName} arrow>
                          <Typography
                            style={{
                              fontSize: 16,
                              fontWeight: 600,
                              marginBottom: 4,
                              textAlign: 'start',
                              flex: 1,
                              ...titleStyle,
                            }}
                          >
                            {course.ProgramName}
                          </Typography>
                        </Tooltip>

                        <ArrowForward
                          onClick={() => handleCardClick(course.Id)}
                          style={{
                            color: '#757575',
                            cursor: 'pointer',
                          }}
                        />
                      </Box>

                      <Typography
                        style={{
                          color: '#757575',
                          textAlign: 'start',
                          ...enrolledTextStyle,
                        }}
                      >
                        Students enrolled: {course?.enrolledNumber || '-'}
                      </Typography>

                      {date && course.TermName && (
                        <Box
                          style={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            padding: '4px 12px',
                            backgroundColor: '#F5F5F5',
                            borderRadius: 16,
                            fontSize: 12,
                            fontWeight: 500,
                            color: '#757575',
                          }}
                        >
                          {course.TermName}
                        </Box>
                      )}
                    </Card>
                  ) : (
                    <Card
                      onClick={() => handleCardClick(course.Id)}
                      style={{
                        cursor: 'pointer',
                        boxShadow: '0px 0px 30px 0px #94ADFF1A',
                        padding: 16,
                        borderRadius: '0.75rem',
                        width: '100%',
                        position: 'relative',
                        ...cardStyle,
                      }}
                    >
                      <Grid container alignItems="center" spacing={2}>
                        {icon && (
                          <Grid item md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              style={{
                                width: '2.25rem',
                                height: '2.25rem',
                                borderRadius: 8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                backgroundColor: '#3f51b5',
                                ...iconStyle,
                              }}
                            >
                              {firstLetter}
                            </Box>
                          </Grid>
                        )}
                        <Grid item md={icon ? 7 : 9}>
                          <Box
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              gap: 8,
                            }}
                          >
                            
                              <Typography
                                style={{
                                  fontSize: 16,
                                  fontWeight: 600,
                                  marginBottom: 4,
                                  textAlign: 'start',
                                  flex: 1,
                                  ...titleStyle,
                                }}
                              >
                                {course.ProgramName}
                              </Typography>
                            

                            <ArrowForward
                              onClick={() => handleCardClick(course.Id)}
                              style={{
                                color: '#757575',
                                cursor: 'pointer',
                              }}
                            />
                          </Box>

                          <Typography
                            style={{
                              fontSize: 14,
                              fontWeight: 400,
                              color: '#757575',
                              textAlign: 'start',
                              ...enrolledTextStyle,
                            }}
                          >
                            Students enrolled: {course.enrolledNumber || '-'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Card>
                  )}
                </Grid>
              </Tooltip>
            );
          })}
      </Grid>
    </>
  );
};

export default CourseCardList;
